<script lang="ts" setup>
import { useI18n } from 'vue-i18n'
import axios from 'axios'
import dayjs from 'dayjs'
import { RouterLink, useRoute } from 'vue-router'

import { PaginatedResponse } from '@/types/general'
import { usePaginatedTable } from '@/hooks/table/use-paginated-table'
import { MinimalTransitReport } from '@/types/damage-report'
import { useAuthStore } from '@/stores/auth-store'
import { GetDataParameters } from '@/types/table'
import { datetime } from '@/utils/dates'
import { transitCategoryTypes } from '@/utils/damage-report'

import CrumbsAndActions from '@/components/layout/CrumbsAndActions.vue'
import Breadcrumb from '@/components/layout/Breadcrumb.vue'
import MyTable from '@/components/table/MyTable.vue'
import MyTableColumn from '@/components/table/MyTableColumn.vue'
import MyPanel from '@/components/my-components/MyPanel.vue'
import ActionRowItem from '@/components/table/ActionRowItem.vue'
import MyDateRangePicker from '@/components/my-components/MyDateRangePicker.vue'

interface FetchTransitReportsParams extends Record<string, unknown>, GetDataParameters {
    filter: {
        'start-date': string | undefined
        'end-date': string | undefined
    }
}

const { t } = useI18n()
const authStore = useAuthStore()
const route = useRoute()

const { data, paginationData, params, loading, error, refetch } = usePaginatedTable<
    MinimalTransitReport,
    FetchTransitReportsParams
>(
    async (params) => {
        const response = await axios.get<PaginatedResponse<MinimalTransitReport>>(
            window.route('dmr.company.transit-reports.index', {
                company: authStore.companyId,
            }),
            { params: params },
        )

        return response.data
    },
    {
        filter: {
            'start-date': dayjs().subtract(1, 'month').startOf('day').toDate().toISOString(),
            'end-date': dayjs().endOf('day').toDate().toISOString(),
        },
    },
)

function setFilter(value: string | number | null | undefined, filterName: string) {
    params.value.filter[filterName] = value
    params.value.page = 1
}
</script>

<template>
    <CrumbsAndActions>
        <Breadcrumb current v-text="t('transitReports')" />
    </CrumbsAndActions>

    <RouterView />

    <MyPanel>
        <MyTable
            :error="error"
            :get-data="refetch"
            :loading="loading"
            :pagination-data="paginationData"
            table-id="dmr-transit-reports"
            :rows="data"
            :entity-name="t('transitReports')"
            :has-time-range="!!params.filter['start-date']"
        >
            <template #filters>
                <MyDateRangePicker
                    :filter="params.filter"
                    @start-date-changed="setFilter($event, 'start-date')"
                    @end-date-changed="setFilter($event, 'end-date')"
                />
            </template>

            <template #actionRow="{ row }">
                <RouterLink
                    :to="{
                        name: 'dmr.transit-reports.show',
                        params: { transitReportId: row.id },
                        query: route.query,
                    }"
                >
                    <ActionRowItem>
                        <mdi:eye />
                    </ActionRowItem>
                </RouterLink>
            </template>
            <MyTableColumn :name="t('company')" property="company.name" />
            <MyTableColumn :name="t('haulier')" property="haulierCompany.name" />
            <MyTableColumn :name="t('user')" property="user.name" />
            <MyTableColumn :name="t('transpiredAt')" property="transpiredAt" />
            <template #transpiredAt="{ row }">
                <span v-text="datetime(row.transpiredAt)" />
            </template>
            <MyTableColumn
                v-for="(translation, type) in transitCategoryTypes"
                :key="type"
                :sortable="false"
                :name="t(translation)"
                :property="`categoryTypes.${type}`"
            />
            <template
                v-for="(_, type) in transitCategoryTypes"
                #[`categoryTypes.${type}`]="{ row }"
                :key="type"
            >
                <span v-if="row.categoryTypes.includes(Number(type))">
                    <mdi:check-bold class="text-green-600" />
                </span>
                <span v-else></span>
            </template>
        </MyTable>
    </MyPanel>
</template>
